import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import UserAvatar from './UserAvatar';
import { getIndustryColor } from '../lib/industryColor';

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: '2-digit' })
  : '—';

const BACK_BTN_STYLE = {
  width: '18px', height: '18px', borderRadius: '50%',
  border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)',
  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', padding: 0, outline: 'none', flexShrink: 0, fontSize: '9px',
  transition: 'background 0.2s',
};

// Paso c (02.3-client.md) -- los 3 botones "atrás" (pasos 1/2/3) eran bloques de estilo
// idénticos repetidos 3 veces; el único de industria→cliente llevaba `marginRight` extra.
function BackStepButton({ onClick, label, withMargin }) {
  return (
    <button
      onClick={onClick}
      style={withMargin ? { ...BACK_BTN_STYLE, marginRight: 'var(--space-2)' } : BACK_BTN_STYLE}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
      title={label}
      aria-label={label}
    >
      ←
    </button>
  );
}

// Extraído de ClientsView.jsx (02.3-client.md paso b) -- la fila inline de 4 pasos para crear un
// cliente/operación de "terminales" (distinta de AddOpModal.jsx, confirmado en el sub-plan §1/§7
// riesgo 2: comparten `useCreateOp` pero son flujos genuinamente distintos, no se fusionan).
// Presentacional puro: el container sigue dueño de `wizardData`/`wizardStep`/los 3 grupos de
// autocomplete y sus handlers -- esta pieza solo renderiza el estado actual, sin lógica de
// negocio propia. `gridCols` se pasa por prop para que la fila siga alineada con el resto de la
// tabla sin duplicar `COL`/`gridCols` acá.
export default function AddClientWizardRow({
  gridCols,
  wizardStep,
  wizardData,
  clientMatches,
  clientActiveIdx,
  industryMatches,
  industryActiveIdx,
  operMatches,
  operActiveIdx,
  allUsers,
  clientInputRef,
  industryInputRef,
  transInputRef,
  operInputRef,
  onIndustryInputChange,
  onIndustryKeyDown,
  onSelectIndustry,
  onClientInputChange,
  onClientKeyDown,
  onSelectClient,
  onStartCreateClient,
  onNewClientNameChange,
  onNewClientEmailChange,
  onRegeneratePassword,
  onCreateClient,
  onTransactionChange,
  onTransKeyDown,
  onOperInputChange,
  onOperKeyDown,
  onSelectOper,
  onGoToStep,
  onCancel,
}) {
  const { t } = useTranslation('client');

  return (
    <div className="client-table-row wizard-row"
      style={{
        display: 'grid',
        gridTemplateColumns: gridCols,
        gap: 0,
        background: 'rgba(59, 130, 246, 0.08)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: 'var(--radius-sm)',
        margin: 'var(--space-1) 0',
        boxShadow: '0 0 8px rgba(59, 130, 246, 0.2)'
      }}
      onClick={e => e.stopPropagation()}
    >
      {/* Checkbox cell */}
      <div className="client-cell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '20px' }} />
      </div>

      {/* Producto */}
      <div className="client-cell" style={{ display: 'flex', alignItems: 'center' }}>
        <span className="client-badge" style={{
          backgroundColor: '#f9731615',
          color: '#f97316',
          borderColor: '#f97316',
          border: '1px solid #f9731644',
          padding: '2px var(--space-2)',
          borderRadius: 'var(--radius-sm)',
          fontSize: '10px',
          fontWeight: '600'
        }}>
          terminales
        </span>
      </div>

      {/* Industria */}
      <div className="client-cell" style={{ position: 'relative', overflow: 'visible', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        {wizardStep === 0 ? (
          <div style={{ position: 'relative', width: '100%', zIndex: 100 }}>
            <input
              ref={industryInputRef}
              type="text"
              className="wizard-input"
              placeholder={t('wizard.industryPlaceholder')}
              aria-label={t('wizard.industryAriaLabel')}
              value={wizardData.industria_input}
              onChange={e => onIndustryInputChange(e.target.value)}
              onKeyDown={onIndustryKeyDown}
              style={{
                width: '100%',
                background: '#0d0d0f',
                color: '#fff',
                border: '1px solid rgba(59, 130, 246, 0.6)',
                borderRadius: 'var(--radius-sm)',
                padding: 'var(--space-1) var(--space-2)',
                fontSize: '11px',
                outline: 'none'
              }}
            />
            {industryMatches.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: '#16161a',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 'var(--radius-sm)',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                {industryMatches.map((m, idx) => (
                  <div
                    key={m}
                    onClick={() => onSelectIndustry(m)}
                    style={{
                      padding: 'var(--space-2) var(--space-2)',
                      fontSize: '11px',
                      color: '#fff',
                      cursor: 'pointer',
                      background: idx === industryActiveIdx ? 'rgba(59, 130, 246, 0.25)' : 'transparent',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {m}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {wizardData.industry ? (
              <span className="client-badge" style={{
                backgroundColor: getIndustryColor(wizardData.industry).bg,
                color: getIndustryColor(wizardData.industry).fg,
                borderColor: getIndustryColor(wizardData.industry).border,
                borderWidth: '1px',
                borderStyle: 'solid',
                padding: '2px var(--space-2)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '10px',
                fontWeight: '600'
              }}>
                {wizardData.industry}
              </span>
            ) : (
              <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: 11 }}>—</span>
            )}
          </div>
        )}
      </div>

      {/* Fecha */}
      <div className="client-cell client-cell-date" style={{ color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center' }}>
        {fmtDate(wizardData.operation_date)}
      </div>

      {/* Estatus */}
      <div className="client-cell" style={{ display: 'flex', alignItems: 'center' }}>
        <span className="client-badge prospecto" style={{ backgroundColor: 'rgba(168, 85, 247, 0.2)', color: '#c084fc', borderColor: 'rgba(168, 85, 247, 0.4)' }}>
          {wizardData.status}
        </span>
      </div>

      {/* Cliente */}
      <div className="client-cell" style={{ position: 'relative', overflow: 'visible', display: 'flex', alignItems: 'center' }}>
        {wizardStep === 1 && (
          <BackStepButton
            withMargin
            label={t('wizard.back')}
            onClick={(e) => { e.stopPropagation(); onGoToStep(0); }}
          />
        )}
        {wizardStep === 1 ? (
          <div style={{ position: 'relative', width: '100%', zIndex: 100 }}>
            <input
              ref={clientInputRef}
              type="text"
              className="wizard-input"
              placeholder={t('wizard.clientSearchPlaceholder')}
              aria-label={t('wizard.clientSearchAriaLabel')}
              value={wizardData.cliente_input}
              onChange={e => onClientInputChange(e.target.value)}
              onKeyDown={onClientKeyDown}
              style={{
                width: '100%',
                background: '#0d0d0f',
                color: '#fff',
                border: '1px solid rgba(59, 130, 246, 0.6)',
                borderRadius: 'var(--radius-sm)',
                padding: 'var(--space-1) var(--space-2)',
                fontSize: '11px',
                outline: 'none'
              }}
            />
            {clientMatches.length > 0 ? (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: '#16161a',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 'var(--radius-sm)',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                {clientMatches.map((u, idx) => (
                  <div
                    key={u.id}
                    onClick={() => onSelectClient(u)}
                    style={{
                      padding: 'var(--space-2) var(--space-2)',
                      fontSize: '11px',
                      color: '#fff',
                      cursor: 'pointer',
                      background: idx === clientActiveIdx ? 'rgba(59, 130, 246, 0.25)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)'
                    }}
                  >
                    <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#333', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {(u['link-profile-img-thumb'] || u['link-profile-img']) ? <img src={(u['link-profile-img-thumb'] || u['link-profile-img'])} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" loading="lazy" /> : <span style={{ fontSize: '9px', color: '#fff' }}>{(u.nick || '?')[0].toUpperCase()}</span>}
                    </div>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.nick}</span>
                  </div>
                ))}
              </div>
            ) : wizardData.cliente_input.trim() ? (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: '#16161a',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 'var(--radius-sm)',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
              }}>
                 <div onClick={onStartCreateClient} style={{ padding: 'var(--space-2) var(--space-2)', color: '#3b82f6', cursor: 'pointer', fontSize: '11px' }}>
                     {t('wizard.addClient', { name: wizardData.cliente_input })}
                 </div>
              </div>
            ) : null}
          </div>
        ) : wizardStep === 1.5 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', width: '200px', position: 'absolute', zIndex: 100, background: '#16161a', padding: 'var(--space-2)', border: '1px solid rgba(59, 130, 246, 0.6)', borderRadius: 'var(--radius-sm)' }}>
                <input type="text" value={wizardData.new_client_name || wizardData.cliente_input} onChange={e => onNewClientNameChange(e.target.value)} placeholder={t('wizard.fullNamePlaceholder')} aria-label={t('wizard.fullNamePlaceholder')} className="wizard-input" style={{ width: '100%', background: '#0d0d0f', color: '#fff', border: '1px solid #333', padding: 'var(--space-1) var(--space-2)', fontSize: '11px', outline: 'none' }} />
                <input type="email" value={wizardData.new_client_email || ''} onChange={e => onNewClientEmailChange(e.target.value)} onKeyDown={e => e.key === 'Enter' && onCreateClient()} placeholder={t('wizard.emailPlaceholder')} aria-label={t('wizard.emailPlaceholder')} className="wizard-input" style={{ width: '100%', background: '#0d0d0f', color: '#fff', border: '1px solid #333', padding: 'var(--space-1) var(--space-2)', fontSize: '11px', outline: 'none' }} />
                {/* Fase 42.1: password generado en el frontend, visible en pantalla -- se manda al backend
                    tal cual para que lo guarde hasheado, y se le envía por correo al cliente nuevo. */}
                <input type="text" value={wizardData.new_client_password || ''} readOnly aria-label="Contraseña generada" className="wizard-input" style={{ width: '100%', background: '#0d0d0f', color: '#3b82f6', border: '1px solid #333', padding: 'var(--space-1) var(--space-2)', fontSize: '11px', outline: 'none', fontFamily: 'monospace' }} />
                <div
                    onClick={onRegeneratePassword}
                    style={{ fontSize: '10px', color: '#888', cursor: 'pointer', textAlign: 'right' }}
                >
                    regenerar contraseña
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-1)', marginTop: 'var(--space-1)' }}>
                    <button onClick={() => onGoToStep(1)} style={{ flex: 1, background: 'transparent', border: '1px solid #333', color: '#fff', padding: 'var(--space-1)', fontSize: '11px', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}>{t('wizard.cancel')}</button>
                    <button onClick={onCreateClient} style={{ flex: 1, background: '#3b82f6', border: 'none', color: '#fff', padding: 'var(--space-1)', fontSize: '11px', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}>{t('wizard.create')}</button>
                </div>
            </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {wizardData.client_id ? (
              <UserAvatar
                img={(() => { const u = allUsers.find(u => u.id === wizardData.client_id); return u ? (u['link-profile-img-thumb'] || u['link-profile-img']) : null; })()}
                nick={allUsers.find(u => u.id === wizardData.client_id)?.nick}
              />
            ) : (
              <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: 11 }}>—</span>
            )}
          </div>
        )}
      </div>

      {/* Transacción */}
      <div className="client-cell" style={{ overflow: 'visible', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        {wizardStep === 2 && (
          <BackStepButton
            label={t('wizard.back')}
            onClick={(e) => { e.stopPropagation(); onGoToStep(1); }}
          />
        )}
        {wizardStep === 2 ? (
          <input
            ref={transInputRef}
            type="text"
            className="wizard-input"
            placeholder={t('wizard.establishmentPlaceholder')}
            aria-label={t('wizard.establishmentAriaLabel')}
            value={wizardData.transaction}
            onChange={e => onTransactionChange(e.target.value)}
            onKeyDown={onTransKeyDown}
            style={{
              width: '100%',
              background: '#0d0d0f',
              color: '#fff',
              border: '1px solid rgba(59, 130, 246, 0.6)',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--space-1) var(--space-2)',
              fontSize: '11px',
              outline: 'none'
            }}
          />
        ) : (
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {wizardData.transaction || (wizardStep > 2 ? '—' : '')}
          </div>
        )}
      </div>

      {/* Oper */}
      <div className="client-cell" style={{ position: 'relative', overflow: 'visible', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        {wizardStep === 3 && (
          <BackStepButton
            label={t('wizard.back')}
            onClick={(e) => { e.stopPropagation(); onGoToStep(2); }}
          />
        )}
        {wizardStep === 3 ? (
          <div style={{ position: 'relative', width: '100%', zIndex: 100 }}>
            <input
              ref={operInputRef}
              type="text"
              className="wizard-input"
              placeholder={t('wizard.assignOperatorPlaceholder')}
              aria-label={t('wizard.assignOperatorAriaLabel')}
              value={wizardData.oper_input}
              onChange={e => onOperInputChange(e.target.value)}
              onKeyDown={onOperKeyDown}
              style={{
                width: '100%',
                background: '#0d0d0f',
                color: '#fff',
                border: '1px solid rgba(59, 130, 246, 0.6)',
                borderRadius: 'var(--radius-sm)',
                padding: 'var(--space-1) var(--space-2)',
                fontSize: '11px',
                outline: 'none'
              }}
            />
            {operMatches.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: '#16161a',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 'var(--radius-sm)',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                {operMatches.map((u, idx) => (
                  <div
                    key={u.id}
                    onClick={() => onSelectOper(u)}
                    style={{
                      padding: 'var(--space-2) var(--space-2)',
                      fontSize: '11px',
                      color: '#fff',
                      cursor: 'pointer',
                      background: idx === operActiveIdx ? 'rgba(59, 130, 246, 0.25)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)'
                    }}
                  >
                    <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#333', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {(u['link-profile-img-thumb'] || u['link-profile-img']) ? <img src={(u['link-profile-img-thumb'] || u['link-profile-img'])} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" loading="lazy" /> : <span style={{ fontSize: '9px', color: '#fff' }}>{(u.nick || '?')[0].toUpperCase()}</span>}
                    </div>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.nick}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {wizardData.oper_id ? (
              <UserAvatar
                img={(() => { const u = allUsers.find(u => u.id === wizardData.oper_id); return u ? (u['link-profile-img-thumb'] || u['link-profile-img']) : null; })()}
                nick={allUsers.find(u => u.id === wizardData.oper_id)?.nick}
              />
            ) : (
              <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: 11 }}>—</span>
            )}
          </div>
        )}
      </div>

      {/* Cancel button */}
      <div className="client-cell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCancel();
          }}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(239, 68, 68, 0.55)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-1)',
            borderRadius: 'var(--radius-sm)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(239, 68, 68, 0.55)'; e.currentTarget.style.background = 'transparent'; }}
          title={t('wizard.cancelWizard')}
          aria-label={t('wizard.cancelWizard')}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
